import React, { useEffect, useState } from 'react'
import { Button, Form, Table } from 'react-bootstrap'
import { ReactComponent as ArrowLeft } from "../../images/arrow_left.svg"
import { useDataContext } from './CIContext';
import { GetFlagAndWeightage, handlePasteNumeric, isAnyToastVisible, preventInvalidNumberInput } from './ciUtils';
import { UpdateCIConfigPayload } from './DataModels';
import { FetchCiConfig, UpdateCiConfig, UpdateCiStatus } from './CredibilityIndex.api';
import displayNotification from '../../notification/displayNotification';
import Logger from '../../utils/Logger';
import { CredibilityIndexAction } from './CredibilityIndex.action';
import { CiBandDisplayToResponseKey, CiStatus, PageViewType } from './CredibilityIndex.constants';
import { NotificationType } from '../../notification/notifications.model';
import { toast } from "react-toastify"

const CITable = () => {

    const context = useDataContext();
    const { state: {getCiConfigResponse, isEditingWeightage, editWeightageValues, clientId, isLoading}, dispatch } = context!;
    const { showNotification } = displayNotification()

    const [showCiInSharedReport, setShowCiInSharedReport] = useState(getCiConfigResponse?.publicReportCiShow || false);

    const isDefaultConfigEnabled = getCiConfigResponse?.ciEnabled

    useEffect(() => {
        return () => {
            if (isAnyToastVisible()) {
                toast.dismiss();
            }
        };
    }, [])

     const validateWeightageSum = () => {
                let sum = 0;

                Object.keys(CiBandDisplayToResponseKey).forEach(displayName => {
                    const editedValue = editWeightageValues[displayName];
                    if (editedValue !== undefined) {
                        sum += Number(editedValue);
                    } else {
                        const { weightage } = GetFlagAndWeightage(displayName, getCiConfigResponse?.flagConfig, CiBandDisplayToResponseKey);
                        if (weightage !== '-' && weightage !== undefined) {
                            sum += Number(weightage);
                        }
                    }
                });
                sum = Number(sum.toFixed(2));

                return sum === 1;
            };


    const handleWeightage = () => {        
      
        if (isEditingWeightage) {

            if (!validateWeightageSum()) {
                showNotification(NotificationType.OVERALL_WEIGHTAGE_EXCEEDED);
                return;
            }

            const updatedFlagConfig = { ...getCiConfigResponse?.flagConfig };
            for (const displayName in editWeightageValues) {
                const { responseKey } = GetFlagAndWeightage(displayName, getCiConfigResponse?.flagConfig, CiBandDisplayToResponseKey);
               
                if (updatedFlagConfig[responseKey]) {
                    updatedFlagConfig[responseKey].weightage = Number(editWeightageValues[displayName]);
                }
            }

    
            const payload: UpdateCIConfigPayload = {
                clientId: clientId,
                flagConfiguration: updatedFlagConfig,
                credibilityScoreBand: null,
                exceptionAndEvents: null,
                publicReportShow: showCiInSharedReport
            }

            dispatch({ type: CredibilityIndexAction.IS_LOADING, payload: { isLoading: true } });

            UpdateCiConfig(payload).then(() => {

                showNotification(NotificationType.WEIGHTAGE_UPDATE_SUCCESS);
                dispatch({ type: CredibilityIndexAction.IS_LOADING, payload: { isLoading: false } });
                dispatch({ type: CredibilityIndexAction.TOGGLE_EDIT_WEIGHTAGE});

            }).catch((error) => {
                Logger.error("Error:", error);                
                dispatch({ type: CredibilityIndexAction.IS_LOADING, payload: { isLoading: false } });
            });
            
        }
        else {
            dispatch({
                type: CredibilityIndexAction.TOGGLE_EDIT_WEIGHTAGE
            });
            if (isAnyToastVisible()) {
                toast.dismiss()
            }
        }
    }

    const handleEditBandScore = (displayName: string) => {

         if (displayName === "Overall") {
            dispatch({
                type: CredibilityIndexAction.UPDATE_ACTIVE_PAGE,
                payload: {
                    activePage: PageViewType.OVERALL_BAND_PAGE
                }
            });
            dispatch({
                type: CredibilityIndexAction.SET_OVERALL_BAND_EDITED,
                payload: {
                    isOverallBandEdited: true
                }
            });
        } else {
             const { responseKey, flag, weightage } = GetFlagAndWeightage(displayName, getCiConfigResponse?.flagConfig, CiBandDisplayToResponseKey);

             dispatch({
                 type: CredibilityIndexAction.UPDATE_EDIT_CREDIBILITY_BAND,
                 payload: {
                     editCredibilityBand: {
                         displayName,
                         responseKey,
                         flag,
                         weightage
                     }
                 }
             });

             dispatch({
                 type: CredibilityIndexAction.UPDATE_ACTIVE_PAGE,
                 payload: {
                     activePage: PageViewType.EDIT_BAND_SCORE_PAGE
                 }
             });
         }
        if (isAnyToastVisible()) {
            toast.dismiss()
        }
      
    }

    const handleBackButton = () => {
       dispatch({
            type: CredibilityIndexAction.RESET_STATE,
        });
        if (isAnyToastVisible()) {
            toast.dismiss()
        }
    }

    const getButtonConfig = () => {
        const response = getCiConfigResponse;

        if (!response?.isDefaultConfig) {
            return {
                text: 'SAVE & Enable Default',
                variant: 'primary'
            };
        }
        if (!response?.ciEnabled) {
            return {
                text: 'Enable',
                variant: 'primary'
            };
        }
        return {
            text: 'Disable',
            variant: 'danger'
        };
    };

     const updateCiStatus = (status: string) => {

        dispatch({ type: CredibilityIndexAction.IS_LOADING, payload: { isLoading: true } });

        const payload = {
            clientId: clientId,
            ciStatus: status
        }

         UpdateCiStatus(payload).then(() => {
             FetchCiConfig({ clientId: clientId }).then((response: any) => {
                 dispatch({
                     type: CredibilityIndexAction.SET_CI_CONFIG_RESPONSE,
                     payload: {
                         response: response,
                         activePage: PageViewType.TABLE_PAGE
                     }
                 });
                 dispatch({ type: CredibilityIndexAction.IS_LOADING, payload: { isLoading: false } });
             }).catch((error) => {
                Logger.error("Error:", error);
                dispatch({ type: CredibilityIndexAction.IS_LOADING, payload: { isLoading: false } });
             });
         }).catch((err) => {
             Logger.error("Error:", err);
             dispatch({ type: CredibilityIndexAction.IS_LOADING, payload: { isLoading: false } });
         });

    }

    const handleCI = () => {
        const buttonConfig = getButtonConfig();
        
        if (buttonConfig.text === 'SAVE & Enable Default') {
            updateCiStatus(CiStatus.ENABLE_DEFAULT);
           
        } else if (buttonConfig.text === 'Enable') {
            updateCiStatus(CiStatus.ENABLE);

        } else {
            updateCiStatus(CiStatus.DISABLE);
        }
    }

    const handleEditWeightageValuesChange = (displayName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        dispatch({
            type: CredibilityIndexAction.UPDATE_WEIGHTAGE_EDITING,
            payload: {
                editWeightageValues: {
                    ...editWeightageValues,
                    [displayName]: value
                }
            }
        });
    }

 
    return (
        <div>
            <div className='d-flex justify-content-between align-items-center mt-3 mb-3'>
                <div className='d-flex'>
                    <span className="c-pointer" onClick={handleBackButton}><ArrowLeft /></span>
                    <h4 className='nav-padding'>{isDefaultConfigEnabled ? `Credibility Index band configuration for ClientID: "${clientId}"` : `Default Credibility Index Configuration: "${clientId}"`}</h4>
                </div>

            </div>

            <div className='d-flex justify-content-between align-items-center mb-3'>

                 <Form.Check
                    type="checkbox"
                    id="show-ci-shared-report"
                    label="Show Credibility Index in Shared Report"
                    checked={showCiInSharedReport}
                    onChange={(e:any) => setShowCiInSharedReport(e.target.checked)}
                    disabled={!isEditingWeightage}
                    className='cursor-not-allowed'
                /> 
               
                <Button variant={getButtonConfig().variant} onClick={handleCI}>
                    {getButtonConfig().text}{isLoading && (
                    <span
                        className="spinner-border spinner-border-sm ml-2"
                        role="status"
                        aria-hidden="true"
                    />
                )}
                </Button>
            </div>

            <Table bordered responsive className="text-nowrap">
                <thead className="bg-sky-lighter">
                    <tr>
                        <th>Name</th>
                        <th>
                            <div className="d-flex justify-content-between align-items-center">
                                <span>Weightage</span>
                                {isDefaultConfigEnabled && (
                                    <Button size="sm" onClick={handleWeightage}
                                        disabled={isEditingWeightage && isLoading}
                                    >
                                        {isEditingWeightage ? (
                                            <>
                                                Save
                                                {isLoading && (
                                                    <span
                                                        className="spinner-border spinner-border-sm ml-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                )}
                                            </>
                                        )
                                            : 'Edit'}
                                    </Button>
                                )}
                            </div>
                        </th>
                        {isDefaultConfigEnabled && <th>Band Score</th>}
                    </tr>
                </thead>
                <tbody>
                    {Object.keys(CiBandDisplayToResponseKey).map((displayName) => {
                    
                        const { weightage } = GetFlagAndWeightage(displayName, getCiConfigResponse?.flagConfig, CiBandDisplayToResponseKey);
                        return (
                            <tr key={displayName} className={'TOTAL_SPEECH_DURATION' === displayName ? 'd-none' : ''}>
                                <td>{displayName}</td>
                                <td>
                                    {isEditingWeightage ? (
                                        <Form.Control
                                            type="number"
                                            value={editWeightageValues[displayName] ?? weightage}
                                            onChange={handleEditWeightageValuesChange(displayName)}
                                            className="w-50"
                                            onKeyDown={preventInvalidNumberInput}
                                            onPaste={handlePasteNumeric}
                                        />
                                    ) : (
                                        weightage
                                    )}
                                </td>
                                {isDefaultConfigEnabled && (
                                    <td>
                                        <Button size="sm" className="ml-4" onClick={() => handleEditBandScore(displayName)}>Edit</Button>
                                    </td>
                                )} 
                            </tr>
                        );
                    })}
                    <tr key="overall">
                        <td>Overall</td>
                        <td>1</td>
                        {isDefaultConfigEnabled && (
                            <td>
                                <Button size="sm" className="ml-4" onClick={() => handleEditBandScore("Overall")}>Edit</Button>
                            </td>
                        )}
                    </tr>
                </tbody>
            </Table>
        </div>

              
  )
}

export default CITable